import React from 'react'

/**
 * Worksheet Preview Component
 * Shows a live preview of the worksheet layout
 */
const WorksheetPreview = ({ characters = [], options = {}, translations = {} }) => {
  const t = (key, defaultValue) => translations[key] || defaultValue

  // Render grid lines based on style
  const GridLines = ({ style }) => {
    switch (style) {
      case 'jiuGongGe': // 九宮格 - 3x3 grid
        return (
          <div className="absolute inset-0">
            {/* Horizontal lines */}
            <div className="absolute w-full h-px bg-gray-200" style={{ top: '33.33%' }} />
            <div className="absolute w-full h-px bg-gray-200" style={{ top: '66.67%' }} />
            {/* Vertical lines */}
            <div className="absolute h-full w-px bg-gray-200" style={{ left: '33.33%' }} />
            <div className="absolute h-full w-px bg-gray-200" style={{ left: '66.67%' }} />
          </div>
        )
      case 'miZiGe': // 米字格 - rice character grid
        return (
          <div className="absolute inset-0">
            {/* Cross lines */}
            <div className="absolute w-full h-px bg-gray-200 top-1/2" />
            <div className="absolute h-full w-px bg-gray-200 left-1/2" />
            {/* Diagonal lines */}
            <div className="absolute w-[141%] h-px bg-gray-200 origin-center rotate-45" style={{ top: '50%', left: '-20%' }} />
            <div className="absolute w-[141%] h-px bg-gray-200 origin-center -rotate-45" style={{ top: '50%', left: '-20%' }} />
          </div>
        )
      case 'plain': // 空白格 - no internal lines
        return null
      case 'tianZiGe': // 田字格 - cross grid (default)
      default:
        return (
          <div className="absolute inset-0">
            <div className="absolute w-full h-px bg-gray-200 top-1/2" />
            <div className="absolute h-full w-px bg-gray-200 left-1/2" />
          </div>
        )
    }
  }

  // Character grid box component
  const CharacterBox = ({ char }) => (
    <div className="border border-gray-300 rounded-lg p-3 bg-white">
      {/* Main character with grid */}
      <div className="relative w-20 h-20 mx-auto mb-2 border-2 border-gray-400">
        {/* Grid lines based on selected style */}
        <GridLines style={options.gridStyle} />
        {/* Character */}
        <span className="absolute inset-0 flex items-center justify-center text-4xl font-serif text-gray-800">
          {char.character}
        </span>
      </div>

      {/* Character info */}
      <div className="text-center space-y-1">
        {options.showZhuyin && (
          <div className="text-sm text-gray-600">
            {char.zhuyin || 'ㄓㄨˋ ㄧㄣ'}
          </div>
        )}
        {options.showPinyin && (
          <div className="text-xs text-gray-500">
            {char.pinyin || 'pīn yīn'}
          </div>
        )}
        {options.showEnglish && (
          <div className="text-xs text-blue-600">
            {char.english || 'meaning'}
          </div>
        )}
      </div>

      {/* Practice boxes */}
      {options.showTracingGuide && (
        <div className="mt-2 flex gap-1 justify-center flex-wrap">
          {/* Tracing boxes with fading characters */}
          {[0.5, 0.35, 0.2].map((opacity, i) => (
            <div
              key={i}
              className="w-8 h-8 border border-gray-300 bg-gray-50 relative"
            >
              <span
                className="absolute inset-0 flex items-center justify-center text-base font-serif"
                style={{ color: `rgba(100, 100, 100, ${opacity})` }}
              >
                {char.character}
              </span>
            </div>
          ))}
          {/* Empty practice boxes */}
          {[4, 5, 6].map(i => (
            <div key={i} className="w-8 h-8 border border-gray-300 bg-white" />
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Preview Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <h3 className="text-sm font-medium text-gray-700">
          {t('preview_title', 'Preview')}
        </h3>
      </div>

      {/* Preview Content */}
      <div className="p-4 min-h-[400px]">
        {characters.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[350px] text-gray-400">
            <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-center">
              {t('preview_empty', 'Enter characters on the left to see a preview')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Character count */}
            <div className="text-xs text-gray-500 text-right">
              {t('preview_count', '%{count} characters').replace('%{count}', characters.length)}
            </div>

            {/* Character grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {characters.slice(0, 6).map((char, idx) => (
                <CharacterBox key={idx} char={char} />
              ))}
            </div>

            {/* More characters indicator */}
            {characters.length > 6 && (
              <div className="text-center text-sm text-gray-500 py-2 border-t border-dashed border-gray-200">
                {t('preview_more', '+%{count} more characters in PDF').replace('%{count}', characters.length - 6)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Footer */}
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <p className="text-xs text-gray-500 text-center">
          {t('preview_note', 'PDF will include all selected characters')}
        </p>
      </div>
    </div>
  )
}

export default WorksheetPreview
